import React, { useState } from 'react';
import { downloadsAPI } from '../../services/api';
import Swal from 'sweetalert2';
import {
  X,
  User,
  Calendar,
  Settings,
  Search,
  ChevronDown,
  ChevronUp,
  Info,
  Download,
  AlertTriangle,
  CheckCircle,
  Loader2,
  FileText,
  Filter
} from 'lucide-react';

const DownloadModal = ({ clients, onClose }) => {
  const [formData, setFormData] = useState({
    clientId: '',
    fechaInicio: '',
    fechaFin: '',
    tipoDescarga: 'Todos',
    tipoSolicitud: 'CFDI',
    tipoComprobante: 'Todos',
    estadoComprobante: 'Todos',
    rfcEmisor: '',
    rfcReceptor: '',
    rfcACuentaTerceros: '',
    uuid: '',
    complemento: 'Todos'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.clientId) {
      setError('Debe seleccionar un cliente');
      return false;
    }

    if (!formData.fechaInicio || !formData.fechaFin) {
      setError('Debe especificar el período de descarga');
      return false;
    }

    const fechaInicio = new Date(formData.fechaInicio);
    const fechaFin = new Date(formData.fechaFin);
    const hoy = new Date();

    if (fechaInicio > hoy) {
      setError('La fecha de inicio no puede ser futura');
      return false;
    }

    if (fechaFin > hoy) {
      setError('La fecha de fin no puede ser futura');
      return false;
    }

    if (fechaInicio > fechaFin) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin');
      return false;
    }

    const diffTime = Math.abs(fechaFin - fechaInicio);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 365) {
      setError('El período de descarga no puede ser mayor a 1 año');
      return false;
    }

    return true;
  };

  const formatDateForDisplay = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'Pending': 'Pendiente',
      'Submitted': 'Enviado al SAT',
      'InProgress': 'En Progreso',
      'Completed': 'Completado',
      'Failed': 'Fallido'
    };
    return statusTexts[status] || status;
  };

  const showSuccessAlert = (responseData) => {
    const clientName = clients.find(c => c.id === formData.clientId);
    const clientDisplay = clientName ? `${clientName.rfc} - ${clientName.razonSocial}` : responseData.clienteRfc;

    Swal.fire({
      icon: 'success',
      title: '¡Descarga Generada Exitosamente!',
      html: `
        <div style="text-align: left; margin: 20px 0;">
          <p><strong>📋 ID de Descarga:</strong><br>
          <code style="background: #f1f1f1; padding: 2px 6px; border-radius: 3px; font-size: 12px;">${responseData.id}</code></p>
          
          <p><strong>🏢 Cliente:</strong><br>${clientDisplay}</p>
          
          <p><strong>📅 Período:</strong><br>
          ${formatDateForDisplay(responseData.fechaInicio)} - ${formatDateForDisplay(responseData.fechaFin)}</p>
          
          <p><strong>📊 Estado:</strong><br>
          <span style="color: #28a745; font-weight: bold;">${getStatusText(responseData.status)}</span></p>
          
          <p><strong>⚙️ Configuración:</strong><br>
          • Tipo: ${responseData.tipoDescarga}<br>
          • Solicitud: ${responseData.tipoSolicitud}<br>
          • Comprobante: ${responseData.tipoComprobante}</p>
          
          <p><strong>🕐 Creado:</strong><br>${formatDateForDisplay(responseData.createdAt)}</p>
          
          ${responseData.satRequestIds && responseData.satRequestIds.length > 0 ?
          `<p><strong>🔗 IDs SAT:</strong><br>
            <small style="color: #666;">${responseData.satRequestIds.filter(id => id).join(', ')}</small></p>`
          : ''
        }
        </div>
        
        <div style="background: #e3f2fd; padding: 15px; border-radius: 5px; margin-top: 15px;">
          <p style="margin: 0; color: #1976d2; font-size: 14px;">
            <strong>ℹ️ Información:</strong><br>
            La descarga ha sido enviada al SAT y se procesará automáticamente. 
            Puedes ver el progreso en la lista de descargas.
          </p>
        </div>
      `,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#007bff',
      width: '600px',
      customClass: {
        popup: 'swal-wide'
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        clientId: formData.clientId,
        fechaInicio: new Date(formData.fechaInicio + 'T00:00:00').toISOString(),
        fechaFin: new Date(formData.fechaFin + 'T23:59:59').toISOString(),
        tipoDescarga: formData.tipoDescarga,
        tipoSolicitud: formData.tipoSolicitud,
        tipoComprobante: formData.tipoComprobante,
        estadoComprobante: formData.estadoComprobante,
        rfcEmisor: formData.rfcEmisor || '',
        rfcReceptor: formData.rfcReceptor || '',
        rfcACuentaTerceros: formData.rfcACuentaTerceros || '',
        uuid: formData.uuid || '',
        complemento: formData.complemento
      };

      console.log('Enviando datos:', submitData);

      const response = await downloadsAPI.create(submitData);

      showSuccessAlert(response.data);

      setTimeout(() => {
        onClose(true);
      }, 100);

    } catch (error) {
      console.error('Error detallado:', error.response?.data);

      Swal.fire({
        icon: 'error',
        title: 'Error al Generar Descarga',
        text: error.response?.data?.message || error.response?.data?.detail || 'Error al crear descarga',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#dc3545'
      });

      setError(error.response?.data?.message || error.response?.data?.detail || 'Error al crear descarga');
    } finally {
      setLoading(false);
    }
  };

  const hasActiveAdvancedFilters = formData.rfcEmisor || formData.rfcReceptor || formData.rfcACuentaTerceros || formData.uuid;
  const today = new Date().toISOString().split('T')[0];
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
  const minDate = fiveYearsAgo.toISOString().split('T')[0];

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <Download className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Nueva Descarga Masiva
            </h2>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Error Alert */}
          {error && (
            <div className="alert alert-error mb-6">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button
                onClick={() => setError('')}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Información Básica */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 pb-3 border-b-2 border-blue-500">
                <User className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Información Básica
                </h3>
              </div>

              <div>
                <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cliente *
                </label>
                <select
                  id="clientId"
                  name="clientId"
                  className="input-field"
                  value={formData.clientId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccionar cliente...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.rfc} - {client.razonSocial}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fechaInicio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha Inicio *
                  </label>
                  <input
                    type="date"
                    id="fechaInicio"
                    name="fechaInicio"
                    className="input-field"
                    value={formData.fechaInicio}
                    onChange={handleChange}
                    min={minDate}
                    max={today}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="fechaFin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha Fin *
                  </label>
                  <input
                    type="date"
                    id="fechaFin"
                    name="fechaFin"
                    className="input-field"
                    value={formData.fechaFin}
                    onChange={handleChange}
                    min={minDate}
                    max={today}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Configuración de Descarga */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 pb-3 border-b-2 border-green-500">
                <Settings className="w-5 h-5 text-green-500" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Configuración de Descarga
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tipoDescarga" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Descarga
                  </label>
                  <select
                    id="tipoDescarga"
                    name="tipoDescarga"
                    className="input-field"
                    value={formData.tipoDescarga}
                    onChange={handleChange}
                  >
                    <option value="Todos">Todos</option>
                    <option value="Emitidos">Emitidos</option>
                    <option value="Recibidos">Recibidos</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="tipoSolicitud" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Solicitud
                  </label>
                  <select
                    id="tipoSolicitud"
                    name="tipoSolicitud"
                    className="input-field"
                    value={formData.tipoSolicitud}
                    onChange={handleChange}
                  >
                    <option value="CFDI">CFDI</option>
                    <option value="Metadata">Metadata</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="tipoComprobante" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Comprobante
                  </label>
                  <select
                    id="tipoComprobante"
                    name="tipoComprobante"
                    className="input-field"
                    value={formData.tipoComprobante}
                    onChange={handleChange}
                  >
                    <option value="Todos">Todos</option>
                    <option value="I">Ingreso</option>
                    <option value="E">Egreso</option>
                    <option value="T">Traslado</option>
                    <option value="N">Nómina</option>
                    <option value="P">Pago</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="estadoComprobante" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estado del Comprobante
                  </label>
                  <select
                    id="estadoComprobante"
                    name="estadoComprobante"
                    className="input-field"
                    value={formData.estadoComprobante}
                    onChange={handleChange}
                  >
                    <option value="Vigente">Vigente</option>
                    <option value="Cancelado">Cancelado</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="complemento" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Complemento
                  </label>
                  <select
                    id="complemento"
                    name="complemento"
                    className="input-field"
                    value={formData.complemento}
                    onChange={handleChange}
                  >
                    <option value="Todos">Todos</option>
                    <option value="Nomina">Nómina</option>
                    <option value="Pagos">Pagos</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Acordeón de Filtros Avanzados */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
              {/* Header del Acordeón */}
              <button
                type="button"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`w-full p-4 flex items-center justify-between transition-all duration-300 ${showAdvancedFilters
                    ? 'bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700'
                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <Search className="w-5 h-5 text-primary-500" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Filtros Avanzados (Opcionales)
                  </h3>
                  {hasActiveAdvancedFilters && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400">
                      ACTIVOS
                    </span>
                  )}
                </div>
                <div className={`transform transition-transform duration-300 ${showAdvancedFilters ? 'rotate-180' : ''}`}>
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                </div>
              </button>

              {/* Contenido del Acordeón */}
              <div className={`transition-all duration-300 overflow-hidden ${showAdvancedFilters ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                <div className="p-6 bg-gray-50 dark:bg-gray-800/50 space-y-6">
                  <div className="alert alert-warning">
                    <Info className="w-5 h-5 flex-shrink-0" />
                    <div>
                      <strong>💡 Tip:</strong> Estos filtros son opcionales y permiten hacer búsquedas más específicas.
                      Déjalos vacíos para obtener todos los comprobantes del período.
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="rfcEmisor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        RFC Emisor
                      </label>
                      <input
                        type="text"
                        id="rfcEmisor"
                        name="rfcEmisor"
                        className="input-field uppercase"
                        value={formData.rfcEmisor}
                        onChange={handleChange}
                        placeholder="Ej: AAA010101AAA"
                        maxLength="13"
                      />
                    </div>

                    <div>
                      <label htmlFor="rfcReceptor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        RFC Receptor
                      </label>
                      <input
                        type="text"
                        id="rfcReceptor"
                        name="rfcReceptor"
                        className="input-field uppercase"
                        value={formData.rfcReceptor}
                        onChange={handleChange}
                        placeholder="Ej: BBB020202BBB"
                        maxLength="13"
                      />
                    </div>

                    <div>
                      <label htmlFor="rfcACuentaTerceros" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        RFC A Cuenta de Terceros
                      </label>
                      <input
                        type="text"
                        id="rfcACuentaTerceros"
                        name="rfcACuentaTerceros"
                        className="input-field uppercase"
                        value={formData.rfcACuentaTerceros}
                        onChange={handleChange}
                        placeholder="Ej: CCC030303CCC"
                        maxLength="13"
                      />
                    </div>

                    <div>
                      <label htmlFor="uuid" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        UUID (Folio Fiscal)
                      </label>
                      <input
                        type="text"
                        id="uuid"
                        name="uuid"
                        className="input-field uppercase"
                        value={formData.uuid}
                        onChange={handleChange}
                        placeholder="Ej: 12345678-1234-1234-1234-123456789012"
                        maxLength="36"
                      />
                    </div>
                  </div>

                  {hasActiveAdvancedFilters && (
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            rfcEmisor: '',
                            rfcReceptor: '',
                            rfcACuentaTerceros: '',
                            uuid: ''
                          });
                        }}
                        className="btn-ghost text-sm"
                      >
                        <Filter className="w-4 h-4 mr-2" />
                        Limpiar Filtros Avanzados
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Información importante */}
            <div className="card p-6 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-700">
              <div className="flex items-start space-x-3">
                <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3">
                    ℹ️ Información importante:
                  </h4>
                  <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                    <li className="flex items-start space-x-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>El período máximo de descarga es de 1 año</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Las fechas no pueden ser futuras</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Los filtros avanzados son opcionales pero permiten descargas más específicas</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>El proceso puede tardar varios minutos dependiendo del volumen</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Recibirás notificaciones sobre el progreso de la descarga</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={() => onClose(false)}
            disabled={loading}
            className="btn-ghost"
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Generando descarga...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Generar Descarga</span>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;